import { Box } from "@mui/material";
import { DotLegalCheckbox, DotLegalDatePicker, DotLegalFullScreenSpinner, DotLegalHeader, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import ProcessingActivityReportDialogHeader from "../processingActivityReportDialogHeader/ProcessingActivityReportDialogHeader.tsx";
import { CustomerReportType } from "../../customer/addEditCustomerDialog/AddEditCustomer.types.ts";
import { BodyTypography, DateBox, getDataBasis, HeaderBodyTypography, HeaderBox, TextBoxWrapper } from "../ProcessingActivityReport.tsx";
import SpanBox from "../SpanBox.tsx";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import { getNumberOfDataSubjects } from "../../processingActivity/processingActivityOverview/ProcessingActivityOverview.hooks.ts";
import { ProcessingActivityStatusManagement } from "../../auth/userContextProvider/UserContextProvider.types.ts";
import React, { Fragment } from "react";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "../../localization/useTranslation.ts";
import { useApprovalOfQualityProjectReport } from "./ApprovalOfQualityProjectReport.hooks.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { ProcessingActivityStatus } from "../../processingActivity/processingActivities/ProcessingActivities.types.ts";

export interface ApprovalOfQualityProjectReportProps {
    processingActivityId?: string;
    isPDF: boolean;
    processingActivityStatus: ProcessingActivityStatus | undefined;
}

function ApprovalOfQualityProjectReport(props: ApprovalOfQualityProjectReportProps) {
    const { permissions, customerName } = useUserContext();
    const { translateString, translateDataSubjectsEnum, translateDate, getDateTimePickerLanguage } = useTranslation();
    const { isLoading, data, reportUpdater } = useApprovalOfQualityProjectReport(props);

    const disableFields =
        permissions.processingActivityStatusManagement === ProcessingActivityStatusManagement.Draft &&
        props.processingActivityStatus !== ProcessingActivityStatus.Draft &&
        props.processingActivityStatus !== ProcessingActivityStatus.AwaitingResearcher;

    return (
        <Box>
            {isLoading ? (
                <Box height={"70vh"}>
                    <DotLegalFullScreenSpinner />
                </Box>
            ) : (
                <Box sx={{ paddingLeft: "8px", paddingRigth: "8px" }}>
                    {props.isPDF && <ProcessingActivityReportDialogHeader reportType={CustomerReportType.ApprovalOfQualityProject} isPdf />}
                    <Box sx={{ marginTop: props.isPDF ? 2 : 0 }}>
                        <HeaderBox>
                            <BodyTypography>
                                <HeaderBodyTypography fontSize={16}>{translateString("projectTitel") + ": "}</HeaderBodyTypography>
                                <SpanBox fontSize={16}>{data?.processingActivityName}</SpanBox>
                            </BodyTypography>
                        </HeaderBox>
                    </Box>
                    <HeaderBox>
                        <HeaderBodyTypography>{translateString("approvelNumber") + ": "}</HeaderBodyTypography>
                        <BodyTypography>{data?.approvalNumber}</BodyTypography>
                    </HeaderBox>
                    <HeaderBox>
                        <BodyTypography>
                            <HeaderBodyTypography>{translateString("groupEntities") + ": "}</HeaderBodyTypography>
                            <SpanBox>{data?.groupCompanies.join(", ")}</SpanBox>
                        </BodyTypography>
                    </HeaderBox>
                    <Box className="page-break-inside">
                        <HeaderBox>
                            <BodyTypography>
                                <HeaderBodyTypography>{translateString("description") + ": "}</HeaderBodyTypography>
                                <SpanBox>
                                    <MentionInputRender value={data?.description ?? ""} />
                                </SpanBox>
                            </BodyTypography>
                        </HeaderBox>
                    </Box>

                    <Box className="avoid-page-break" mb={3}>
                        <Fragment>
                            {props.isPDF ? (
                                <>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("approvedEmployees")}</HeaderBodyTypography>
                                    </Box>
                                    <BodyTypography>
                                        <SpanBox>{data?.approvedEmployees}</SpanBox>
                                    </BodyTypography>
                                </>
                            ) : (
                                <TextBoxWrapper>
                                    <DotLegalTextField
                                        label={translateString("approvedEmployees")}
                                        value={data?.approvedEmployees ?? ""}
                                        debounce
                                        multiline
                                        rows={8}
                                        onChange={reportUpdater.onApprovedEmployeesChange}
                                        disabled={disableFields}
                                    />
                                </TextBoxWrapper>
                            )}
                        </Fragment>
                    </Box>

                    <HeaderBox>
                        <BodyTypography>
                            <HeaderBodyTypography>{translateString("purposes") + ": "}</HeaderBodyTypography>
                            <SpanBox>{data?.purposes.join(", ")}</SpanBox>
                        </BodyTypography>
                    </HeaderBox>

                    <HeaderBox>
                        <HeaderBodyTypography>{translateString("NumberOfSubjects") + ": "}</HeaderBodyTypography>
                        <BodyTypography>
                            {data && data?.numberOfSubjects
                                ? getNumberOfDataSubjects(translateDataSubjectsEnum, data?.numberOfSubjects, data?.noOfSubjects)
                                : undefined}
                        </BodyTypography>
                    </HeaderBox>

                    <Box sx={{ display: "flex", gap: 5 }}>
                        {props.isPDF ? (
                            <HeaderBox>
                                <HeaderBodyTypography>{translateString("approvelDate") + ": "}</HeaderBodyTypography>
                                <BodyTypography>{data?.approvalDate ? translateDate(data?.approvalDate!) : ""}</BodyTypography>
                            </HeaderBox>
                        ) : (
                            <Box>
                                <HeaderBodyTypography>{translateString("approvelDate") + ": "}</HeaderBodyTypography>
                                <DateBox>
                                    <DotLegalDatePicker
                                        label=""
                                        value={data?.approvalDate ?? null}
                                        onChange={reportUpdater.onApprovalDateChange}
                                        disabled={permissions.processingActivityStatusManagement! !== ProcessingActivityStatusManagement.Full}
                                        language={getDateTimePickerLanguage()}
                                        noMargin
                                    />
                                </DateBox>
                            </Box>
                        )}
                        {props.isPDF ? (
                            <HeaderBox>
                                <HeaderBodyTypography>{translateString("theProjectsEndDate") + ": "}</HeaderBodyTypography>
                                <BodyTypography>{data?.projectEndDate ? translateDate(data?.projectEndDate!) : ""}</BodyTypography>
                            </HeaderBox>
                        ) : (
                            <Box>
                                <HeaderBodyTypography>{translateString("theProjectsEndDate") + ": "}</HeaderBodyTypography>
                                <DateBox>
                                    <DotLegalDatePicker
                                        label=""
                                        value={data?.projectEndDate ?? null}
                                        onChange={reportUpdater.onProjectEndDateChange}
                                        language={getDateTimePickerLanguage()}
                                        noMargin
                                        disabled={disableFields}
                                    />
                                </DateBox>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 5 }} mb={2}>
                        {props.isPDF ? (
                            <HeaderBox>
                                <HeaderBodyTypography>{translateString("journalPeriodFrom") + ": "}</HeaderBodyTypography>
                                <BodyTypography>{data?.approvalDate ? translateDate(data?.fromJournalPeriod!) : ""}</BodyTypography>
                            </HeaderBox>
                        ) : (
                            <Box>
                                <HeaderBodyTypography>{translateString("journalPeriodFrom") + ": "}</HeaderBodyTypography>
                                <DateBox>
                                    <DotLegalDatePicker
                                        label=""
                                        value={data?.fromJournalPeriod ?? null}
                                        onChange={reportUpdater.onFromJournalPeriodChange}
                                        language={getDateTimePickerLanguage()}
                                        noMargin
                                        disabled={disableFields}
                                    />
                                </DateBox>
                            </Box>
                        )}
                        {props.isPDF ? (
                            <HeaderBox>
                                <HeaderBodyTypography>{translateString("journalPeriodTo") + ": "}</HeaderBodyTypography>
                                <BodyTypography>{data?.projectEndDate ? translateDate(data?.toJournalPeriod!) : ""}</BodyTypography>
                            </HeaderBox>
                        ) : (
                            <Box>
                                <HeaderBodyTypography>{translateString("journalPeriodTo") + ": "}</HeaderBodyTypography>
                                <DateBox>
                                    <DotLegalDatePicker
                                        label=""
                                        value={data?.toJournalPeriod ?? null}
                                        onChange={reportUpdater.onToJournalPeriodChange}
                                        language={getDateTimePickerLanguage()}
                                        noMargin
                                        disabled={disableFields}
                                    />
                                </DateBox>
                            </Box>
                        )}
                    </Box>

                    <Box className="avoid-page-break" mb={2}>
                        <Grid container spacing={1} marginTop={0}>
                            <Grid size={6}>
                                <Fragment>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("dataRetrievedFromDepartments")}</HeaderBodyTypography>
                                    </Box>
                                    {props.isPDF ? (
                                        <BodyTypography>
                                            <SpanBox>{data?.dataProvidingDepartments}</SpanBox>
                                        </BodyTypography>
                                    ) : (
                                        <TextBoxWrapper>
                                            <DotLegalTextField
                                                label={""}
                                                value={data?.dataProvidingDepartments ?? ""}
                                                debounce
                                                multiline
                                                placeholder={translateString("dataRetrievedFromDepartments")}
                                                rows={8}
                                                onChange={reportUpdater.onDataRetrievedFromDepartmentsChange}
                                                disabled={disableFields}
                                            />
                                        </TextBoxWrapper>
                                    )}
                                </Fragment>
                            </Grid>
                            <Grid size={6}>
                                <Fragment>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("journalInformationGathered")}</HeaderBodyTypography>
                                    </Box>
                                    {props.isPDF ? (
                                        <BodyTypography>
                                            <SpanBox>{data?.journalDataRetrieved}</SpanBox>
                                        </BodyTypography>
                                    ) : (
                                        <TextBoxWrapper>
                                            <DotLegalTextField
                                                onChange={reportUpdater.onJournalInformationGatheredChange}
                                                label={""}
                                                placeholder={translateString("journalInformationGathered")}
                                                value={data?.journalDataRetrieved ?? ""}
                                                debounce
                                                multiline
                                                rows={8}
                                                disabled={disableFields}
                                            />
                                        </TextBoxWrapper>
                                    )}
                                </Fragment>
                            </Grid>
                        </Grid>
                    </Box>

                    <Box className="avoid-page-break" sx={{ marginBottom: props.isPDF ? 1 : 1 }}>
                        {props.isPDF ? (
                            <BodyTypography>
                                <HeaderBodyTypography>{translateString("dataBaseAndSources") + ": "}</HeaderBodyTypography>

                                {getDataBasis(translateString, data)}

                                <Box sx={{ marginLeft: "-12px", marginTop: "-20px" }}>
                                    <DotLegalCheckbox
                                        checked={data?.departmentManagementHasSetProjectForApproval ?? false}
                                        label={translateString("departmentManagementHasRecommendedProject")}
                                        disabled={disableFields}
                                    />
                                </Box>
                            </BodyTypography>
                        ) : (
                            <Fragment>
                                <Box>
                                    <HeaderBodyTypography>{translateString("dataBaseAndSources") + ": "}</HeaderBodyTypography>
                                </Box>

                                {getDataBasis(translateString, data)}

                                <Box sx={{ marginLeft: "-12px", marginTop: "-20px" }}>
                                    <DotLegalCheckbox
                                        checked={data?.departmentManagementHasSetProjectForApproval ?? false}
                                        onChange={reportUpdater.onDepartmentManagementApprovalChange}
                                        label={translateString("departmentManagementHasRecommendedProject")}
                                        disabled={disableFields}
                                    />
                                </Box>
                            </Fragment>
                        )}
                    </Box>
                    <Box className="avoid-page-break" mb={2}>
                        <Grid container spacing={1} marginTop={0}>
                            <Grid size={6}>
                                <Fragment>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("primaryResponsible")}</HeaderBodyTypography>
                                    </Box>
                                    {props.isPDF ? (
                                        <BodyTypography>
                                            <SpanBox>{data?.primaryResponsible}</SpanBox>
                                        </BodyTypography>
                                    ) : (
                                        <TextBoxWrapper>
                                            <DotLegalTextField
                                                label={""}
                                                value={data?.primaryResponsible ?? ""}
                                                debounce
                                                multiline
                                                placeholder={translateString("contactPersonPlaceHolder")}
                                                rows={8}
                                                onChange={reportUpdater.onResponsibleChange}
                                                disabled={disableFields}
                                            />
                                        </TextBoxWrapper>
                                    )}
                                </Fragment>
                            </Grid>
                            <Grid size={6}>
                                <Fragment>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("contact")}</HeaderBodyTypography>
                                    </Box>
                                    {props.isPDF ? (
                                        <BodyTypography>
                                            <SpanBox>{data?.contact}</SpanBox>
                                        </BodyTypography>
                                    ) : (
                                        <TextBoxWrapper>
                                            <DotLegalTextField
                                                onChange={reportUpdater.onContactPersonChange}
                                                label={""}
                                                placeholder={translateString("contactPersonPlaceHolder")}
                                                value={data?.contact ?? ""}
                                                debounce
                                                multiline
                                                rows={8}
                                                disabled={disableFields}
                                            />
                                        </TextBoxWrapper>
                                    )}
                                </Fragment>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box className={"avoid-page-break"}>
                        {props.isPDF ? (
                            <>
                                <Box>
                                    <HeaderBodyTypography>{translateString("note")}</HeaderBodyTypography>
                                </Box>
                                <BodyTypography>
                                    <SpanBox>{data?.note}</SpanBox>
                                </BodyTypography>
                            </>
                        ) : (
                            <DotLegalTextField
                                multiline
                                isLoading={isLoading}
                                debounce
                                rows={8}
                                label={translateString("note")}
                                value={data?.note ?? ""}
                                onChange={reportUpdater.onNoteChange}
                                disabled={disableFields}
                            />
                        )}
                    </Box>
                    <Box className="avoid-page-break" mb={3} mt={3}>
                        <Fragment>
                            {props.isPDF ? (
                                <>
                                    <Box>
                                        <HeaderBodyTypography>{translateString("signaturesFromExternalHospitals")}</HeaderBodyTypography>
                                    </Box>
                                    <BodyTypography>
                                        <SpanBox>{data?.signatures}</SpanBox>
                                    </BodyTypography>
                                </>
                            ) : (
                                <TextBoxWrapper>
                                    <DotLegalTextField
                                        label={translateString("signaturesFromExternalHospitals")}
                                        value={data?.signatures ?? ""}
                                        debounce
                                        multiline
                                        rows={8}
                                        onChange={reportUpdater.onSignaturesChange}
                                        disabled={disableFields}
                                    />
                                </TextBoxWrapper>
                            )}
                        </Fragment>
                    </Box>
                    <DotLegalHeader headerStyle={"small"} fontStyle="italic">
                        {translateString("processingActivityQualityReportNote")}
                    </DotLegalHeader>
                </Box>
            )}
        </Box>
    );
}

export default ApprovalOfQualityProjectReport;
